import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdSchool } from 'react-icons/md';
import { FaPlay, FaArrowLeft } from 'react-icons/fa';

// Lazy loading del CourseViewer
const CourseViewer = lazy(() => import('./CourseViewer'));

// Metadata ligera de cursos
const coursesMetadata = {
  "contratos-menores": {
    title: "Contratos Menores",
    description: "Domina completamente los procesos de contratación menores a 8 UIT",
    color: "#0066CC",
    totalDuration: "14 horas",
    thumbnail: "/cm.png"
  },
  "pac": {
    title: "Plan Anual de Contrataciones (PAC)",
    description: "Aprende a analizar y aprovechar el PAC",
    color: "#7C3AED",
    totalDuration: "18 horas",
    thumbnail: null
  },
  "seace": {
    title: "SEACE",
    description: "Guía completa para navegar el SEACE",
    color: "#DC2626",
    totalDuration: "24 horas",
    thumbnail: "/seace.png"
  }
};

const LoadingCourse = ({ color = '#3b82f6' }) => (
  <div style={{
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000'
  }}>
    <div style={{ textAlign: 'center', color: 'white' }}>
      <div style={{
        width: '60px',
        height: '60px',
        border: '6px solid rgba(255,255,255,0.1)',
        borderTop: `6px solid ${color}`,
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
        margin: '0 auto 1rem'
      }} />
      <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>Cargando curso...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

const CoursePlatform = ({ user = { id: 'user123', email: 'consultingnuvia@gmail.com', name: 'Usuario Demo' } }) => {
  const { courseId } = useParams(); // Obtener courseId de la URL
  const navigate = useNavigate();
  
  // Estado inicial cargado SINCRÓNICAMENTE desde localStorage
  const getInitialCourse = () => {
    // Si viene courseId por parámetro, usarlo
    if (courseId) {
      return courseId;
    }
    
    // Si no, buscar en localStorage
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(`current_course_${user.email}`);
    } catch {
      return null;
    }
  };

  const getInitialProgress = () => {
    if (typeof window === 'undefined') return {};
    try {
      const saved = localStorage.getItem(`course_progress_${user.email}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  // Estados inicializados con valores de localStorage (NO hay delay)
  const [selectedCourse, setSelectedCourse] = useState(getInitialCourse);
  const [progress, setProgress] = useState(getInitialProgress);
  const [viewReady, setViewReady] = useState(false);

  // Sincronizar con el parámetro de URL
  useEffect(() => {
    if (courseId && courseId !== selectedCourse) {
      setSelectedCourse(courseId);
      localStorage.setItem(`current_course_${user.email}`, courseId);
    }
  }, [courseId, selectedCourse, user.email]);

  // Marcar como listo después de la primera renderización
  useEffect(() => {
    setViewReady(true);
  }, []);

  const getCourseProgress = (courseId) => {
    let completed = 0;
    const courseProgress = progress[courseId];
    
    if (courseProgress) {
      Object.values(courseProgress).forEach(moduleProgress => {
        completed += moduleProgress.length;
      });
    }
    
    // Estimado simple: asumimos ~50 videos por curso
    const total = 50;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const handleCourseSelect = (courseId) => {
    setSelectedCourse(courseId);
    localStorage.setItem(`current_course_${user.email}`, courseId);
    navigate(`/cursos/player/${courseId}`);
  };

  const handleBackToAcademia = () => {
    navigate('/cursos/academia');
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
    localStorage.removeItem(`current_course_${user.email}`);
    navigate('/cursos/academia');
  };

  // Si hay curso seleccionado, NUNCA mostrar la vista de cursos
  if (selectedCourse) {
    return (
      <Suspense fallback={<LoadingCourse color={coursesMetadata[selectedCourse]?.color} />}>
        <CourseViewer 
          courseId={selectedCourse}
          user={user}
          onBack={handleBackToAcademia}
          progress={progress}
          setProgress={setProgress}
        />
      </Suspense>
    );
  }

  // Vista de selección de cursos (solo si NO hay curso seleccionado)
  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa', 
      padding: '2rem',
      opacity: viewReady ? 1 : 0,
      transition: 'opacity 0.2s ease'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Botón volver a academia */}
        <button
          onClick={handleBackToAcademia}
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            color: '#4b5563',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.color = '#4b5563';
          }}
        >
          <FaArrowLeft size={14} />
          Volver a Academia
        </button>

        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <MdSchool size={32} color="#3b82f6" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Mis Cursos</h1>
            <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280' }}>Selecciona un curso para continuar</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {Object.entries(coursesMetadata).map(([courseId, course]) => {
            const progressPercent = getCourseProgress(courseId);
            
            return (
              <div 
                key={courseId} 
                onClick={() => handleCourseSelect(courseId)}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.3s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{
                  height: '360px',
                  background: course.thumbnail ? `url(${course.thumbnail})` : course.color,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.4))',
                    zIndex: 1
                  }} />
                  
                  <div style={{
                    width: '60px', 
                    height: '60px',
                    background: 'rgba(255,255,255,0.95)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <FaPlay size={24} color={course.color} />
                  </div>
                </div>

                <div style={{ padding: '1.25rem' }}>
                  <h5 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {course.title}
                  </h5>
                  <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    {course.description}
                  </p>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      height: '6px',
                      background: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${progressPercent}%`,
                        background: course.color,
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                    <small style={{ color: '#718096' }}>
                      {progressPercent}% completado
                    </small>
                  </div>

                  <button style={{
                    width: '100%',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    background: course.color,
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 4px 12px ${course.color}66`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <FaPlay style={{ marginRight: '0.5rem' }} />
                    {progressPercent > 0 ? 'Continuar' : 'Comenzar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CoursePlatform;